import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import CancelablePromise from 'cancelable-promise';
import {useConstCallback} from './useConstCallback';

const loadRegular = (src: string) => {
	return new CancelablePromise<HTMLImageElement>(
		(resolve, reject, onCancel) => {
			const image = new Image();

			const handleError = (e: ErrorEvent) => reject(e);

			const handleLoad = () => {
				if (!image.naturalHeight && !image.naturalWidth)
					return reject(new Error('image not found'));
				resolve(image);
			};

			image.addEventListener('error', handleError);
			image.addEventListener('load', handleLoad);

			onCancel(() => {
				image.removeEventListener('error', handleError);
				image.removeEventListener('load', handleLoad);
				image.src = src;
			});

			image.src = src;
		},
	);
};

const loadXHR = (src: string) => {
	const xmlHTTP = new XMLHttpRequest();
	return {
		xmlHTTP,
		promise: new CancelablePromise<Blob>((resolve, reject, onCancel) => {
			xmlHTTP.open('GET', src, true);
			xmlHTTP.responseType = 'arraybuffer';

			const handleError = (e: ProgressEvent<XMLHttpRequestEventTarget>) =>
				reject('Error communicating with server');

			const handleLoad = (e: ProgressEvent<XMLHttpRequestEventTarget>) => {
				if (xmlHTTP.status !== 200) {
					reject(`Reported loaded with non valid status: ${xmlHTTP.status}`);
					return;
				}

				const headers = xmlHTTP.getAllResponseHeaders();
				const matchMime = headers.match(/^Content-Type\:\s*(.*?)$/im);
				const blob = new Blob([xmlHTTP.response], {
					type: matchMime ? matchMime[1] : 'image/jpg',
				});
				resolve(blob);
			};

			xmlHTTP.addEventListener('error', handleError);
			xmlHTTP.addEventListener('load', handleLoad);

			onCancel(() => {
				xmlHTTP.removeEventListener('error', handleError);
				xmlHTTP.removeEventListener('load', handleLoad);
				try {
					xmlHTTP.abort();
				} catch {}
			});

			xmlHTTP.send();
		}),
	};
};

/**
 * @depreacted
 */
export const useImageLoader = (
	disableXHR = false,
	onLoaded?: (src: string) => void,
) => {
	const [, setFallbackUrls] = useState<string[]>([]);
	const [error, setError] = useState<string>();
	const [loading, setLoading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [src, setSrc] = useState<string>();
	const [url, setUrl] = useState<string>();

	const lastUrlRef = useRef<string>();

	const handleImageLoaded = useConstCallback((src: string) => onLoaded?.(src));

	// On each error, move on tothe next fallback url
	useEffect(() => {
		if (error == null) return;
		console.warn('trying fallback if available');
		setFallbackUrls((fallbackUrls) => {
			if (!fallbackUrls.length) {
				console.warn('fallback unavailable');
				setLoading(false);
				return fallbackUrls;
			}
			const next = [...fallbackUrls];
			setUrl(next.shift());
			return next;
		});
	}, [error]);

	// When it is time to load the image
	useLayoutEffect(() => {
		if (!url) return;

		setError(undefined);
		setLoading(true);
		setProgress(0);

		let cancel: () => void;

		// image.src
		if (disableXHR) {
			const promise = loadRegular(url);
			cancel = promise.cancel;
			promise
				.then((image) => {
					setSrc(url);
					handleImageLoaded(url);
					setUrl(undefined);
				})
				.catch((error: string) => setError(error))
				.finally(() => {
					setLoading(false);
					cancel = () => {};
				}, true);

			return () => {
				setLoading(false);
				cancel();
			};
		}

		// xhr
		const {xmlHTTP, promise} = loadXHR(url);
		promise
			.then((blob) => {
				const blobUrl = URL.createObjectURL(blob);
				lastUrlRef.current &&
					blobUrl !== lastUrlRef.current &&
					URL.revokeObjectURL(lastUrlRef.current);
				lastUrlRef.current = blobUrl;
				setSrc(blobUrl);
				handleImageLoaded(blobUrl);
				setUrl(undefined);
			})
			.catch((error: string) => setError(error))
			.finally(() => {
				setLoading(false);
				cancel = () => {};
			}, true);

		cancel = () => promise.cancel();

		const handleLoadEnd = () => setProgress(100);

		const handleProgress = (e: ProgressEvent<XMLHttpRequestEventTarget>) =>
			e.lengthComputable && setProgress((e.loaded / e.total) * 100);

		xmlHTTP.addEventListener('loadend', handleLoadEnd);
		xmlHTTP.addEventListener('progress', handleProgress);

		return () => {
			xmlHTTP.removeEventListener('loadend', handleLoadEnd);
			xmlHTTP.removeEventListener('progress', handleProgress);
			setLoading(false);
			cancel();
		};
	}, [disableXHR, url]);

	useEffect(() => {
		return () => {
			lastUrlRef.current && URL.revokeObjectURL(lastUrlRef.current);
		};
	}, []);

	const load = useConstCallback((url: string | string[]) => {
		if (!Array.isArray(url)) url = [url];

		const theUrlToLoad = url.shift();
		if (!theUrlToLoad) return;

		setSrc(undefined);
		setLoading(false);
		setError(undefined);
		setFallbackUrls(url);
		setUrl(theUrlToLoad);
	});

	return {
		error,
		loading,
		loadingUrl: url,
		progress,
		src,

		load,
	};
};

export default useImageLoader;
